<script setup>
import { ref } from 'vue';
import { onMounted } from 'vue';
import { PollingManager } from '@/utils/ProvenancePollingManager';
import { inject } from 'vue';
import Spinner from '../Spinner.vue';
import { createPostMessageChannel, safeParsePostMessage } from '@/utils/postMessageHelpers';
import { getItemId } from '@/utils/auth';
import { captureException } from '@sentry/vue';

const COLLABORA_ONLINE_URL = import.meta.env.VITE_COLLABORA_ONLINE_URL;
const PROVENANCE_API = import.meta.env.VITE_PROVENANCE_API;

const props = defineProps(['presentationId', 'isSidebarOpen'])
const emit = defineEmits(['onSidebarClosed', 'onSidebarOpened', 'image-clicked'])

const snippets = ref([]);
const matchingElements = ref([]);
const selectedSnippetText = ref(null);
/* const isOpen = ref(false); */
const isOpen = ref(false);
const highlightedSnippet = ref(null);
const pollingManager = ref(null);
const currSlideContent = ref([]);
const pollingDone = ref(false);
const currSlideHasSnippets = ref(false);

const { editorIframeRef } = inject('editorIframeRef');

function open() {
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
}

function isOpened() {
  return isOpen.value;
}

onMounted(() => {
  window.addEventListener('message', handleMessage);

  pollingManager.value = new PollingManager(
    PROVENANCE_API,
    getItemId(props.presentationId),
    updateSnippets.bind(this),
    setPollingStatus.bind(this)
  );

  pollingManager.value.startPolling();
});

const updateSnippets = (fetchedSnippets) => {
  snippets.value = fetchedSnippets;
  if (!selectedSnippetText.value) {
    matchingElements.value = getFirstMatchingSnippets();
  } else {
    matchingElements.value = snippets.value.filter(
      textElement => selectedSnippetText.value.toLowerCase().includes(textElement.text.toLowerCase())
    );
  }
};

const setPollingStatus = (pollingDoneStatus) => {
  pollingDone.value = pollingDoneStatus;
};

const handleMessage = async (e) => {
  const msg = safeParsePostMessage(e.data);
  if (msg.MessageId === "Selected_Shape_Changed") {
    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
      "MessageId": "CallPythonScript",
      "SendTime": Date.now(),
      'ScriptFile': 'UpdateSidebarSnippets.py',
      'Function': 'GetTextOfSelectedShape',
      "Values": {}
    }), COLLABORA_ONLINE_URL);
  } else if (msg.MessageId === "Page_Changed") {
    currSlideContent.value = await getCurrentPageTextElements()
    if (currSlideContent.value.length == 0) {
      currSlideContent.value = await getCurrentPageTextElements()
    }
    matchingElements.value = [];
    currSlideHasSnippets.value = false;
    selectedSnippetText.value = null;
    conditionallyToggleSidebar();
  }
  else if (msg.MessageId === "CallPythonScript-Result") {
    if (
      msg.Values.commandName.startsWith("vnd.sun.star.script:UpdateSidebarSnippets.py$GetTextOfSelectedShape")
      && msg.Values.success === true
    ) {
      const postMessageResult = JSON.parse(msg.Values.result.value);
      selectedSnippetText.value = postMessageResult.data?.selected_shape_text;
      console.log("Selected text: " + selectedSnippetText.value);

      matchingElements.value = [];
      if (selectedSnippetText.value) {
        matchingElements.value = snippets.value.filter(
          textElement => selectedSnippetText.value.toLowerCase().includes(textElement.text.toLowerCase())
        );
      }
    }
  }
}

function getFirstMatchingSnippets() {
  let matchingSnippets = [];
  currSlideContent.value.forEach((textElement) => {
    if (matchingSnippets.length == 0) {
      try {
        matchingSnippets.push.apply(
          matchingSnippets,
          snippets.value.filter((snippetsGroup) => {
            return textElement.text.toLowerCase().includes(snippetsGroup.text.toLowerCase());
          }
        ));
      } catch (err) {
        captureException(
          err,
          {
            level: "error",
            extra: {
              snippets: snippets.value,
            }
          }
        );
      }
    }
  });
  return matchingSnippets;
}

async function conditionallyToggleSidebar() {
  matchingElements.value = getFirstMatchingSnippets();
  if (matchingElements.value.length > 0) {
    currSlideHasSnippets.value = true;
    if (!props.isSidebarOpen && !isOpened()) {
      emit('onSidebarOpened');
    }
  } else {
    if (props.isSidebarOpen && isOpened()) {
      emit('onSidebarClosed');
    }
  }
}

const getCurrentPageTextElements = async () => {
  const response = await createPostMessageChannel(
    editorIframeRef.value.contentWindow,
    COLLABORA_ONLINE_URL,
    JSON.stringify({
      "MessageId": "CallPythonScript",
      "SendTime": Date.now(),
      "ScriptFile": "UpdateSidebarSnippets.py",
      "Function": "GetAllText",
      "Values": {}
    }),
    (msg) => msg.MessageId === 'CallPythonScript-Result'
        && msg.Values.commandName.startsWith("vnd.sun.star.script:UpdateSidebarSnippets.py$GetAllText")
  );
  try {
    const result = JSON.parse(response.Values.result.value);
    return result.data;
  } catch (err) {
    captureException(
      err,
      {
        level: "error",
        extra: {
          response: response,
        }
      }
    );
    return [];
  }
};

function addSource(sourcesText) {
  const iframeWindow = editorIframeRef.value.contentWindow;
  iframeWindow.postMessage(JSON.stringify({
    "MessageId": "CallPythonScript",
    "SendTime": Date.now(),
    "ScriptFile": "InsertText.py",
    "Function": "InsertText",
    "Values": {
      "text": { "type": "string", "value": "Source: " + sourcesText },
    }
  }), COLLABORA_ONLINE_URL);
}

defineExpose({
  open, close, isOpened
});
</script>


<template>
  <div id="snippetsSidebar" v-if="isOpen"
       style="position: absolute;top: 0%;bottom: 0%;right: 0%; left: 0%;display: flex; flex-direction: column;">
    <div class="px-4 py-3 is-flex is-justify-content-space-between is-align-content-center has-background-white">
      <header class="has-text-black has-text-weight-semibold is-size-5">
        Provenance
      </header>
      <a class="is-flex is-align-content-center" @click="() => { emit('onSidebarClosed'); }">
        <img src="@/assets/images/x-close.svg">
      </a>
    </div>
    <div style="overflow-y: auto; overflow-x: hidden; flex-grow: 1;">
      <div v-if="matchingElements.length > 0" class="py-4">
        <div v-for="(element, index) in matchingElements" class="content">
          <div v-if="element.sources" class="is-flex is-justify-content-start mx-4 my-3">
            <Button label="Add source" @click="addSource(element.sources)" />
          </div>
          <h5 class="px-4 has-text-grey-darker">{{ element.text }}</h5>
          <div class="card mx-2 my-2 has-background-white-ter" v-for="(snippet, index) in element.snippets" style="overflow-y:auto;">
            <div class="card-content px-4 pt-4 pb-0">
              <p class="content is-size-7 has-text-grey"
                 :class="{ 'has-text-weight-bold': selectedSnippetText == snippet.elementText }">
                {{ snippet.text }}
                <a @click="() => { emit('image-clicked', snippet.public_url) }" class="is-flex is-flex-direction-column-reverse mb-3" style="float: inline-end;width:20%; aspect-ratio: 0.7;overflow-y: hidden; position: relative;">
                  <img v-if="snippet.public_url" class="snippet-image" :src="snippet.public_url"
                       style="border: solid 1px lightgray; position: absolute; top: 0;">
                </a>
              </p>
              <div class="is-flex is-flex-direction-column-reverse px-4 pb-4" style="width:80%;position: absolute;bottom: 0;left: 0;">
                <div class="is-flex is-align-items-center">
                  <figure class="image is-24x24 is-flex-shrink-0">
                    <img class="snippet-favico" :src="snippet.favicon" style="border: medium black">
                  </figure>
                  <h7 class="snippet-url ml-2 has-text-black has-text-weight-bold is-size-7" style="">
                    <a :href="snippet.link" target="_blank" style="color:unset;">
                      {{ snippet.title }}
                    </a>
                  </h7>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="!pollingDone" class="is-flex is-flex-direction-column is-justify-content-center is-align-items-center is-size-5 has-text-grey"
           style="height:100%">
        Loading provenance
        <Spinner class="m-4" style="font-size: 1.5rem" />
      </div>
      <div v-else-if="pollingDone && !currSlideHasSnippets" class="is-flex is-justify-content-center is-align-items-center is-size-5 has-text-grey"
           style="height:100%">
        No provenance for this slide
      </div>
      <div v-else-if="pollingDone && currSlideHasSnippets" class="is-flex is-justify-content-center is-align-items-center is-size-5 has-text-grey"
           style="height:100%">
        Select a text box to see snippets
      </div>
    </div>
  </div>
</template>

<style scoped>
 #snippetsSidebar div:has(header) {
   border-bottom: 1px solid lightgray;
 }

 .card {
   border: double 1px transparent;
   border-radius: 20px;
   background-origin: border-box;
   background-clip: content-box, border-box;
 }

 .card-footer {
   border-top: unset;
 }

 h7.snippet-url {
   display: -webkit-box;
   -webkit-line-clamp: 2;
   -webkit-box-orient: vertical;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: unset;
 }
</style>
